import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const TYPE_AR = { institution: 'مؤسسة', person: 'شخص' };

export default function Sponsors() {
  const { api, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = {
    name: '', type: 'institution', contact_name: '', contact_phone: '',
    contact_email: '', notes: '', is_active: true,
  };
  const [form, setForm] = useState(empty);

  const load = () => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (typeFilter) qs.set('type', typeFilter);
    setLoading(true);
    api(`/sponsors?${qs}`).then(setList).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, [typeFilter]);

  const canEdit = ['admin', 'supervisor', 'team'].includes(user?.role);
  const canDelete = ['admin', 'supervisor'].includes(user?.role);

  const openNew = () => { setEditing(null); setForm(empty); setShowModal(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, type: s.type, contact_name: s.contact_name || '',
      contact_phone: s.contact_phone || '', contact_email: s.contact_email || '',
      notes: s.notes || '', is_active: !!s.is_active,
    });
    setShowModal(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api(`/sponsors/${editing.id}`, { method: 'PUT', body: JSON.stringify(form) });
        toast.success('تم تحديث الداعم');
      } else {
        await api('/sponsors', { method: 'POST', body: JSON.stringify(form) });
        toast.success('تم إنشاء الداعم');
      }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (s) => {
    if (!window.confirm(`حذف الداعم "${s.name}"؟`)) return;
    try {
      await api(`/sponsors/${s.id}`, { method: 'DELETE' });
      toast.success('تم الحذف');
      load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>الداعمون / أصحاب المشاريع</h2>
        {canEdit && <button className="btn btn-primary" onClick={openNew}>+ داعم جديد</button>}
      </div>

      <div className="filters">
        <input
          className="form-input"
          placeholder="بحث بالاسم..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
        <select className="form-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">كل الأنواع</option>
          {Object.entries(TYPE_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>🔍 بحث</button>
      </div>

      {loading ? (
        <div className="loader">جاري التحميل...</div>
      ) : list.length === 0 ? (
        <div className="card"><div className="empty"><div className="empty-icon">🤝</div>لا يوجد داعمون</div></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {list.map(s => (
            <div key={s.id} className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{s.name}</div>
                <span className={`badge ${s.type === 'institution' ? 'badge-info' : 'badge-in_progress'}`}>{TYPE_AR[s.type]}</span>
              </div>
              {s.contact_name && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>👤 {s.contact_name}</div>
              )}
              {s.contact_email && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>✉️ {s.contact_email}</div>
              )}
              {s.contact_phone && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>📞 {s.contact_phone}</div>
              )}
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                📂 {s.total_projects || 0} مشروع
                {s.active_projects > 0 && <span style={{ color: 'var(--success)' }}> ({s.active_projects} نشط)</span>}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                <button className="btn btn-sm btn-outline" onClick={() => navigate(`/reports/sponsor/${s.id}`)}>📊 تقرير</button>
                <button className="btn btn-sm btn-outline" onClick={() => navigate(`/projects?sponsor_id=${s.id}`)}>المشاريع</button>
                {canEdit && <button className="btn btn-sm btn-outline" onClick={() => openEdit(s)}>تعديل</button>}
                {canDelete && <button className="btn btn-sm btn-outline" onClick={() => remove(s)} style={{ color: 'var(--danger)' }}>حذف</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{editing ? 'تعديل داعم' : 'داعم جديد'}</div>
            <div className="form-group">
              <label className="form-label">الاسم *</label>
              <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">النوع *</label>
                <select className="form-select" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  {Object.entries(TYPE_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">الحالة</label>
                <select
                  className="form-select"
                  value={form.is_active ? '1' : '0'}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.value === '1' }))}
                >
                  <option value="1">نشط</option>
                  <option value="0">معطّل</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">اسم جهة الاتصال</label>
                <input className="form-input" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">البريد</label>
                <input className="form-input" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">الهاتف</label>
              <input className="form-input" value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">ملاحظات</label>
              <textarea className="form-textarea" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={save} disabled={!form.name.trim()}>
                {editing ? 'حفظ' : 'إنشاء'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
